import React, { useEffect, useMemo, useRef, useState } from "react"
import { Button } from "antd"
import { GoogleMaps, Icon } from "components"
import { getStatusLabel, Status } from "utils/status"
import { deliveryZonesToPolygons, filterZoneToPolygons } from "utils/geojson"

// Hex colors for map markers, matching Ant Design preset intent
const STATUS_MARKER_COLORS: Record<string, string> = {
	registered: "#8c8c8c",
	address_confirmation: "#FAAD14",
	picking: "#13C2C2",
	packing: "#2F54EB",
	processing: "#1890FF",
	aggregation: "#36CFC9",
	loading: "#1890FF",
	ready_to_van: "#52C41A",
	dispatching: "#FA8C16",
	on_transit: "#722ED1",
	delivered: "#52C41A",
	not_delivered: "#FF4D4F",
	cancelled: "#FF4D4F",
	returned: "#FA8C16",
	dispute: "#EB2F96",
	billing: "#A0D911",
	partial_delivered: "#FA541C",
	mixed_failed: "#FF4D4F",
}

interface OrdersStatusMapProps {
	orders: any[]
	height?: string
	onRemoveOrder?: (orderId: string) => void
	deliveryZones?: any[]
	filterZone?: { geojson?: any } | null
}

const OrdersStatusMap: React.FC<OrdersStatusMapProps> = ({
	orders,
	height = "350px",
	onRemoveOrder,
	deliveryZones,
	filterZone,
}) => {
	const mapRef = useRef<google.maps.Map | null>(null)
	const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null)
	const [isFullscreen, setIsFullscreen] = useState(false)
	const [showMapPOIs, setShowMapPOIs] = useState(false)
	const [currentZoom, setCurrentZoom] = useState(12)

	// Build map markers from orders
	const mapMarkers = useMemo(() => {
		const markerScale = Math.max(3, Math.round(currentZoom * 0.5))

		return orders
			.map((order) => {
				if (
					typeof order.formattedAddress?.location?.lat !== "number" ||
					typeof order.formattedAddress?.location?.lng !== "number"
				)
					return null

				const status = order.orderFlow?.status || "registered"
				const markerColor = STATUS_MARKER_COLORS[status] || "#8c8c8c"

				return {
					lat: order.formattedAddress.location.lat,
					lng: order.formattedAddress.location.lng,
					icon: {
						path: 0, // google.maps.SymbolPath.CIRCLE
						fillColor: markerColor,
						fillOpacity: 1,
						scale: markerScale,
						strokeColor: "white",
						strokeWeight: 2,
					},
					order,
					buttons: onRemoveOrder
						? [
								{
									label: "Remove from selection",
									color: "#ff4d4f",
									onClick: () => onRemoveOrder(order._id),
								},
							]
						: undefined,
				}
			})
			.filter((marker): marker is NonNullable<typeof marker> => !!marker)
	}, [orders, currentZoom, onRemoveOrder])

	// Build polygons for the contract's delivery zones (if any), then draw the
	// active geo filter on top so it stays readable over them.
	const zonePolygons = useMemo(
		() => [
			...deliveryZonesToPolygons(deliveryZones),
			...filterZoneToPolygons(filterZone),
		],
		[deliveryZones, filterZone]
	)

	// Compute bounding box for map (depends only on orders, not mapMarkers, to avoid zoom loop)
	const boundingBox = useMemo(() => {
		const coords = orders
			.map((order) => order.formattedAddress?.location)
			.filter(
				(loc): loc is { lat: number; lng: number } =>
					typeof loc?.lat === "number" && typeof loc?.lng === "number"
			)
		if (coords.length === 0) return undefined
		return {
			south: Math.min(...coords.map((c) => c.lat)),
			west: Math.min(...coords.map((c) => c.lng)),
			north: Math.max(...coords.map((c) => c.lat)),
			east: Math.max(...coords.map((c) => c.lng)),
		}
	}, [orders])

	// Compute legend: unique statuses with counts
	const legendItems = useMemo(() => {
		const statusCounts: Record<string, number> = {}
		for (const order of orders) {
			const status = order.orderFlow?.status || "registered"
			statusCounts[status] = (statusCounts[status] || 0) + 1
		}
		return Object.entries(statusCounts).map(([status, count]) => ({
			status,
			color: STATUS_MARKER_COLORS[status] || "#8c8c8c",
			label: getStatusLabel(status as Status),
			count,
		}))
	}, [orders])

	// Wrap mapRef so we detect when the map loads
	const mapProxyRef = useMemo(
		() => ({
			get current() {
				return mapRef.current
			},
			set current(val: google.maps.Map | null) {
				mapRef.current = val
				if (val) setMapInstance(val)
			},
		}),
		[]
	)

	// Fit map bounds when orders change
	useEffect(() => {
		if (mapInstance && boundingBox) {
			mapInstance.fitBounds(boundingBox, 40)
		}
	}, [mapInstance, boundingBox])

	// Track zoom changes to scale markers
	useEffect(() => {
		if (!mapInstance) return
		const listener = mapInstance.addListener("zoom_changed", () => {
			const zoom = mapInstance.getZoom()
			if (zoom != null) setCurrentZoom(zoom)
		})
		return () => google.maps.event.removeListener(listener)
	}, [mapInstance])

	const handleRecenter = () => {
		if (mapInstance && boundingBox) {
			mapInstance.fitBounds(boundingBox, 40)
		}
	}

	if (mapMarkers.length === 0) return null

	const controlButtonStyle: React.CSSProperties = {
		height: 28,
		padding: "0 8px",
		display: "flex",
		alignItems: "center",
		gap: 4,
		backgroundColor: "white",
		border: "none",
		borderRadius: 2,
		boxShadow: "rgba(0, 0, 0, 0.3) 0px 1px 4px -1px",
		cursor: "pointer",
		fontSize: 12,
		color: "rgba(0, 0, 0, 0.87)",
	}

	return (
		<div className="OrderDetailPanel__section">
			<div
				style={{
					position: "relative",
					...(isFullscreen
						? {
								position: "fixed",
								top: 0,
								left: 0,
								width: "100vw",
								height: "100vh",
								zIndex: 1000,
								backgroundColor: "#fff",
								display: "flex",
								flexDirection: "column",
							}
						: {}),
				}}
			>
				<GoogleMaps
					overlay
					overlaySize="small"
					overlayTrigger="click"
					zoom={12}
					boundingBox={boundingBox}
					mapRef={mapProxyRef}
					mapContainerStyle={{
						width: "100%",
						height: isFullscreen ? "100vh" : height,
						borderRadius: isFullscreen ? 0 : 5,
					}}
					options={{
						panControl: false,
						gestureHandling: isFullscreen ? "greedy" : "cooperative",
						fullscreenControl: false,
						clickableIcons: showMapPOIs,
						styles: [
							{
								featureType: "poi",
								elementType: "all",
								stylers: [{ visibility: showMapPOIs ? "on" : "off" }],
							},
							{
								featureType: "transit",
								elementType: "all",
								stylers: [{ visibility: showMapPOIs ? "on" : "off" }],
							},
						],
					}}
					markers={mapMarkers}
					polygons={zonePolygons}
				/>

				{/* Custom map controls */}
				<div
					style={{
						position: "absolute",
						top: 10,
						right: 10,
						display: "flex",
						flexDirection: "column",
						gap: 4,
						zIndex: 1,
					}}
				>
					<Button
						size="small"
						onClick={() => setIsFullscreen(!isFullscreen)}
						style={controlButtonStyle}
					>
						<Icon
							name={isFullscreen ? "close" : "frame"}
							style={{ fontSize: 14, color: "rgba(0, 0, 0, 0.6)" }}
						/>
						<span>{isFullscreen ? "Exit" : "Fullscreen"}</span>
					</Button>
					<Button
						size="small"
						onClick={handleRecenter}
						style={controlButtonStyle}
					>
						<Icon
							name="location"
							style={{ fontSize: 14, color: "rgba(0, 0, 0, 0.6)" }}
						/>
						<span>Recenter</span>
					</Button>
					<Button
						size="small"
						onClick={() => setShowMapPOIs(!showMapPOIs)}
						style={{
							...controlButtonStyle,
							backgroundColor: showMapPOIs ? "#1890ff" : "white",
							color: showMapPOIs ? "white" : "rgba(0, 0, 0, 0.87)",
						}}
					>
						<Icon
							name={showMapPOIs ? "eye" : "eye-outline"}
							style={{
								fontSize: 14,
								color: showMapPOIs ? "white" : "rgba(0, 0, 0, 0.6)",
							}}
						/>
						<span>POIs</span>
					</Button>
				</div>

				{/* Legend */}
				<div
					style={{
						display: "flex",
						flexWrap: "wrap",
						gap: "10px",
						fontSize: 12,
						marginTop: 8,
						marginBottom: 8,
						...(isFullscreen
							? {
									position: "absolute",
									bottom: 10,
									left: 10,
									backgroundColor: "white",
									padding: "6px 10px",
									borderRadius: 4,
									boxShadow: "rgba(0, 0, 0, 0.3) 0px 1px 4px -1px",
									margin: 0,
								}
							: {}),
					}}
				>
					{legendItems.map((item) => (
						<div
							key={item.status}
							style={{
								display: "flex",
								alignItems: "center",
								gap: 4,
							}}
						>
							<div
								style={{
									width: 10,
									height: 10,
									borderRadius: 2,
									backgroundColor: item.color,
								}}
							/>
							<span>
								{item.label}: <strong>{item.count}</strong>
							</span>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

export default OrdersStatusMap
